import React, { useEffect, useState } from 'react';
import { api } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { BarChart3, TrendingUp, Target, Users } from 'lucide-react';

const COLORS = ['#0055FF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6B7280'];

export default function ReportingPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse-subtle text-center py-20 text-[#71717A]">Chargement...</div>;
  if (!stats) return null;

  const statusLabels = {
    a_appeler: 'À appeler', appele: 'Appelé', rdv_obtenu: 'RDV obtenu',
    refus: 'Refus', rappel_demande: 'Rappel', injoignable: 'Injoignable',
    mauvais_numero: 'Mauvais n°', converti: 'Converti'
  };

  const searchLabels = { trouve: 'Trouvés', a_verifier: 'À vérifier', non_trouve: 'Non trouvés' };

  const searchPie = [
    { name: 'Trouvés', value: stats.trouves },
    { name: 'À vérifier', value: stats.a_verifier },
    { name: 'Non trouvés', value: stats.non_trouves },
  ].filter(d => d.value > 0);

  const commercialPie = Object.entries(stats.statuts_commerciaux || {}).map(([k, v]) => ({
    name: statusLabels[k] || k, value: v
  })).filter(d => d.value > 0);

  const perfData = (stats.performance_commerciaux || []).map(p => ({
    name: p.nom?.split(' ')[0] || 'N/A',
    total: p.total, appeles: p.appeles, rdv: p.rdv, convertis: p.convertis,
    taux_rdv: p.total > 0 ? Math.round((p.rdv / p.total) * 100) : 0,
    taux_conv: p.total > 0 ? Math.round((p.convertis / p.total) * 100) : 0,
  }));

  const enrichRate = stats.total_prospects > 0 ? Math.round(((stats.trouves + stats.a_verifier) / stats.total_prospects) * 100) : 0;
  const contactRate = stats.traites > 0 && stats.total_prospects > 0 ? Math.round((stats.traites / stats.total_prospects) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reporting-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Reporting</h1>
        <p className="text-sm text-[#71717A] mt-1">Analyse détaillée de vos performances</p>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border border-[#E5E7EB] shadow-sm kpi-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-[#0055FF]" /><span className="text-xs text-[#71717A]">Taux Enrichissement</span></div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{enrichRate}%</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E7EB] shadow-sm kpi-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[#10B981]" /><span className="text-xs text-[#71717A]">Taux Contact</span></div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{contactRate}%</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E7EB] shadow-sm kpi-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-[#F59E0B]" /><span className="text-xs text-[#71717A]">RDV Obtenus</span></div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{stats.statuts_commerciaux?.rdv_obtenu || 0}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E5E7EB] shadow-sm kpi-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-[#8B5CF6]" /><span className="text-xs text-[#71717A]">Convertis</span></div>
            <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{stats.statuts_commerciaux?.converti || 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search Results Pie */}
        <Card className="border border-[#E5E7EB] shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold mb-4">Résultats Enrichissement</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={searchPie} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {searchPie.map((_, i) => <Cell key={i} fill={['#10B981', '#F59E0B', '#EF4444'][i]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Commercial Status Pie */}
        <Card className="border border-[#E5E7EB] shadow-sm">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold mb-4">Statuts Commerciaux</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={commercialPie} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2} dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {commercialPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Performance by Commercial */}
        {perfData.length > 0 && (
          <Card className="border border-[#E5E7EB] shadow-sm lg:col-span-2">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-4">Performance par Commercial</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={perfData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total" fill="#0055FF" name="Total Assigné" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="appeles" fill="#F59E0B" name="Appelés" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="rdv" fill="#10B981" name="RDV" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="convertis" fill="#8B5CF6" name="Convertis" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Conversion Rates */}
        {perfData.length > 0 && (
          <Card className="border border-[#E5E7EB] shadow-sm lg:col-span-2">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-4">Taux de Conversion</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#E5E7EB]">
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">Commercial</th>
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">Total</th>
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">Appelés</th>
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">RDV</th>
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">Convertis</th>
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">Taux RDV</th>
                    <th className="text-left py-2 px-3 text-[#71717A] font-medium">Taux Conv.</th>
                  </tr></thead>
                  <tbody>
                    {perfData.map(p => (
                      <tr key={p.name} className="border-b border-[#E5E7EB]/50">
                        <td className="py-2 px-3 font-medium">{p.name}</td>
                        <td className="py-2 px-3">{p.total}</td>
                        <td className="py-2 px-3">{p.appeles}</td>
                        <td className="py-2 px-3">{p.rdv}</td>
                        <td className="py-2 px-3">{p.convertis}</td>
                        <td className="py-2 px-3 font-medium text-[#0055FF]">{p.taux_rdv}%</td>
                        <td className="py-2 px-3 font-medium text-[#10B981]">{p.taux_conv}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
